import './MonitoredDocumentCompare.styles.scss';

import React, { Component } from 'react';

import { Error } from '../../shared/Error/Error';
import { History } from '../../../helpers';
import { LoadingIndicator } from '../../shared/LoadingIndicator/LoadingIndicator';
import Parser from 'html-react-parser';
import PropTypes from 'prop-types';
import Swal from 'sweetalert2';
import { formatDate } from '../../../utils/dateUtils';

function createDiffPromiseWorker(data) {
    const worker = new Worker('/assets/js/htmldiff-worker.js');
    const promise = new Promise(function (resolve, reject) {
        worker.onmessage = function (event) {
            worker.terminate();
            resolve(event.data);
        };
        worker.onerror = function (event) {
            worker.terminate();
            reject(event.error);
        };
        worker.postMessage(data);
    });

    return { promise, worker };
}

// COMPONENT
class MonitoredDocumentCompare extends Component {
    constructor(props) {
        super(props);

        this.onAnalyzeVersion = this.onAnalyzeVersion.bind(this);
        this.onIgnoreVersion = this.onIgnoreVersion.bind(this);
        this.onStopMonitoring = this.onStopMonitoring.bind(this);
        this.onClose = this.onClose.bind(this);

        this.handleKeyPress = this.handleKeyPress.bind(this);

        this.state = {
            diffHtml: '',
            isDiffing: false,
        };
    }

    handleKeyPress(event) {
        switch (event.key) {
            case 'i':
                this.ignoreVersion();
                break;
            case 's':
                this.stopMonitoring();
                break;
            case 'r':
                this.analyzeVersion();
                break;
        }
    }

    componentDidCatch(error, info) {
        Swal({
            title: `There was an error rendering the document`,
            text: error,
            type: 'error',
        });

        console.error(error, info);
    }

    updateDiffHtml() {
        const { oldHtml, newHtml } = this.props;

        if (oldHtml === newHtml) {
            this.setState({
                diffHtml: oldHtml ? Parser(oldHtml) : '',
                isDiffing: false,
            });
            return;
        }

        this.setState({
            diffHtml: '',
            isDiffing: true,
        });

        const { promise, worker } = createDiffPromiseWorker({ fromHtml: oldHtml, toHtml: newHtml });
        this.worker = worker;
        return promise
            .catch(() => {
                this.setState({
                    isDiffing: false,
                });
            })
            .then(response => {
                this.setState({
                    diffHtml: response ? Parser(response) : '',
                    isDiffing: false,
                });
            });
    }

    componentDidMount() {
        this.props.onInitialLoad();
        document.addEventListener('keydown', this.handleKeyPress, false);
        this.updateDiffHtml();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.oldHtml !== this.props.oldHtml || prevProps.newHtml !== this.props.newHtml) {
            this.worker && this.worker.terminate();
            this.updateDiffHtml();
        }
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleKeyPress, false);
        this.worker && this.worker.terminate();
    }

    ignoreVersion() {
        const { backLink, setMonitoredDocumentValidated } = this.props;

        setMonitoredDocumentValidated(false, 'Changes ignored')
            .then(() => {
                if (backLink) {
                    History.push(backLink);
                } else {
                    History.push('/');
                }
            })
            .catch(err => {
                Swal({
                    title: `Set document validated status failed`,
                    text: err,
                    type: 'error',
                });
            });
    }

    onIgnoreVersion() {
        Swal({
            title: 'Confirm',
            type: 'warning',
            showCancelButton: true,
            focusConfirm: false,
            footer: 'Clicking "OK" will ignore the changes to this document.',
        }).then(result => {
            if (result.dismiss) {
                return;
            }
            this.ignoreVersion();
        });
    }

    analyzeVersion(reason = '') {
        const { backLink, setMonitoredDocumentValidated, createDocumentAnalysis } = this.props;

        createDocumentAnalysis()
            .then(() => {
                setMonitoredDocumentValidated(true, reason)
                    .then(() => {
                        if (backLink) {
                            History.push(backLink);
                        } else {
                            History.push('/');
                        }
                    })
                    .catch(err => {
                        Swal({
                            title: `Set document validated status failed`,
                            text: err,
                            type: 'error',
                        });
                    });
            })
            .catch(err => {
                Swal({
                    title: `Create analysis failed`,
                    text: err,
                    type: 'error',
                });
            });
    }

    onAnalyzeVersion() {
        Swal({
            title: 'Confirm',
            type: 'warning',
            text: 'Any comments (optional)',
            input: 'text',
            showCancelButton: true,
            focusConfirm: false,
            footer: 'Clicking "OK" will trigger an analysis for the vendor associated with this document.',
        }).then(result => {
            if (result.dismiss) {
                return;
            }
            const { value: reason } = result;
            this.analyzeVersion(reason);
        });
    }

    stopMonitoring(reason = '') {
        const {
            backLink,
            setDocumentMonitoringStatus,
            setMonitoredDocumentValidated,
            setMonitoredDocumentActiveStatus,
        } = this.props;

        setDocumentMonitoringStatus()
            .then(() => {
                setMonitoredDocumentActiveStatus()
                    .then(() => {
                        setMonitoredDocumentValidated(false, reason)
                            .then(() => {
                                if (backLink) {
                                    History.push(backLink);
                                } else {
                                    History.push('/');
                                }
                            })
                            .catch(err => {
                                Swal({
                                    title: `Set document validated status failed`,
                                    text: err,
                                    type: 'error',
                                });
                            });
                    })
                    .catch(err => {
                        Swal({
                            title: `Set document active status failed`,
                            text: err,
                            type: 'error',
                        });
                    });
            })
            .catch(err => {
                Swal({
                    title: `Set document monitoring status failed`,
                    text: err,
                    type: 'error',
                });
            });
    }

    onStopMonitoring() {
        Swal({
            title: 'Confirm',
            type: 'warning',
            text: 'Any comments (optional)',
            input: 'text',
            showCancelButton: true,
            focusConfirm: false,
            footer: 'Clicking "OK" will stop monitoring this document for changes.',
        }).then(result => {
            if (result.dismiss) {
                return;
            }

            const { value: reason } = result;
            this.stopMonitoring(reason);
        });
    }

    onClose() {
        const { backLink } = this.props;

        if (backLink) {
            History.push(backLink);
        } else {
            History.push('/');
        }
    }

    render() {
        const { fetched, fetching, failed, oldHtml, newHtml, monitoredDocumentCompare } =
            this.props;

        const { isDiffing, diffHtml } = this.state;

        if (fetching && !fetched) {
            return <LoadingIndicator busy={fetching} />;
        } else if (isDiffing) {
            return <LoadingIndicator busy={isDiffing} />;
        } else if (failed) {
            return <Error message="Failed to fetch monitored document record" />;
        } else if (oldHtml && newHtml && monitoredDocumentCompare) {
            return (
                <div>
                    <div className="card">
                        <div className="card-body">
                            <div className="card-title">
                                <div className="d-flex justify-content-between w-100">
                                    <h4>
                                        {monitoredDocumentCompare.name}
                                        <small className="text-muted">Document Compare</small>
                                    </h4>
                                    <div>
                                        <a
                                            href={monitoredDocumentCompare.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            current document{' '}
                                            <i className="fas fa-external-link-alt"></i>
                                        </a>
                                    </div>
                                    <div>
                                        {monitoredDocumentCompare.newVersion.isValidated ? (
                                            <div>
                                                <button
                                                    onClick={this.onClose}
                                                    className="btn btn-secondary mb-2 ml-2"
                                                >
                                                    Close
                                                </button>
                                            </div>
                                        ) : (
                                            <div
                                                className="btn-group"
                                                role="group"
                                                aria-label="Basic example"
                                            >
                                                <button
                                                    onClick={this.onIgnoreVersion}
                                                    className="btn btn-success"
                                                >
                                                    Ignore Changes
                                                </button>
                                                <button
                                                    onClick={this.onAnalyzeVersion}
                                                    className="btn btn-warning"
                                                >
                                                    Re-Analyze
                                                </button>
                                                <button
                                                    onClick={this.onStopMonitoring}
                                                    className="btn btn-danger"
                                                >
                                                    Stop Monitoring
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {monitoredDocumentCompare.newVersion.conversionNotes &&
                            monitoredDocumentCompare.newVersion.conversionNotes.length && (
                                <div className="alert alert-dismissible alert-warning ml-2 mr-2 mb-2">
                                    <button type="button" className="close" data-dismiss="alert">
                                        &times;
                                    </button>
                                    <h6 className="alert-heading">Document Conversion Notes</h6>
                                    <ul className="mb-0">
                                        {monitoredDocumentCompare.newVersion.conversionNotes.map(
                                            note => (
                                                <li key={note}>{note}</li>
                                            )
                                        )}
                                    </ul>
                                </div>
                            )}
                    </div>
                    <div className="mt-1 d-flex">
                        <div className="w-50 mr-1">
                            <div className="card">
                                <div className="card-header">
                                    {monitoredDocumentCompare.oldVersion.updated
                                        ? formatDate(monitoredDocumentCompare.oldVersion.updated)
                                        : formatDate(
                                              monitoredDocumentCompare.oldVersion.created
                                          )}{' '}
                                </div>
                                <div className="card-body">
                                    <div className="fromVersion">{diffHtml || Parser(oldHtml)}</div>
                                </div>
                            </div>
                        </div>
                        <div className="w-50">
                            <div className="card">
                                <div className="card-header">
                                    {monitoredDocumentCompare.newVersion.updated
                                        ? formatDate(monitoredDocumentCompare.newVersion.updated)
                                        : formatDate(monitoredDocumentCompare.newVersion.created)}
                                </div>
                                <div className="card-body">
                                    <div className="toVersion">{diffHtml || Parser(newHtml)}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        } else {
            return null;
        }
    }
}

MonitoredDocumentCompare.propTypes = {
    onInitialLoad: PropTypes.func.isRequired,
    setMonitoredDocumentValidated: PropTypes.func.isRequired,
    createDocumentAnalysis: PropTypes.func.isRequired,
    setDocumentMonitoringStatus: PropTypes.func.isRequired,
    setMonitoredDocumentActiveStatus: PropTypes.func.isRequired,
    fetched: PropTypes.bool.isRequired,
    fetching: PropTypes.bool.isRequired,
    documentId: PropTypes.string.isRequired,
    oldVersionId: PropTypes.string.isRequired,
    newVersionId: PropTypes.string.isRequired,
    oldHtml: PropTypes.string,
    newHtml: PropTypes.string,
    failed: PropTypes.bool,
    monitoredDocumentCompare: PropTypes.object,
    backLink: PropTypes.object,
};

export default MonitoredDocumentCompare;
